import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Typo } from "@ui/text/Typo";
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter";
import type { ReactNode } from "react";
import { toast } from "react-toastify";
import { GD } from "src/GD";
import { MAX_USER_DETAILS_WIDTH } from "src/constants";
import { useRouter } from "src/services/Router";
import {
	formatMerchantWallet,
	getTransactionAmountPresentation,
	getParentTypeLabel,
	getTransactionDescription,
	getTransactionParties,
	getTransactionTypeLabel,
} from "src/utils/transactionPresentation";
import { CardDetails } from "./CardDetails";
import { getIssuerDisplayName } from "../issuer/issuerIdentity";
import {
	AmountHero,
	FundsFlow,
	MoneyFlow,
	StatusPill,
	TransactionDetailsLive,
	TxIcon,
	formatDateTime,
	issuerMerchantName,
	issuerAmountRows,
	useTransactionDetails,
} from "./TransactionDetailsLive";
import type { ITransactionDetails } from "src/services/transactionDetails";
import { TeamDetails } from "./TeamDetails";
import { UserDetails } from "./UserDetails";
import { backUniversalDialog, openNestedUniversalDialog, S_UNIVERSAL_DIALOG } from "./universalDialog/signals";
import { DS, DetailRow, EntityHeader, Section } from "../ds/Kit";
import { useIsPrivateAccount } from "../private/PrivateAccountContext";

interface TransactionDetailsProps {
	transaction: ICompanyTrxTransaction;
	typeOption?: ICompanyTransactionFilterOption;
	onClose?: () => void;
	onBack?: () => void;
	onTransactions?: (cardUid: string) => void;
	privateCardActionsOnly?: boolean;
}

const CARD_LAST_FOUR_LENGTH = 4;
const TEAM_DETAILS_MAX_WIDTH = 900;


const getMaskedLastFour = (cardMask?: string) => {
	const digits = (cardMask || "").replace(/\D/g, "");
	const lastFour = digits.slice(-CARD_LAST_FOUR_LENGTH);
	return lastFour ? `****${lastFour}` : "";
};

const getAccountDetailsRows = (account: ICompanyTransactionAccountResponse, privateView: boolean) => {
	const details = account.card || account.buyer || account.company;
	return [
		["Account", `#${account.account_id}`],
		["Kind", account.kind],
		["Issuer", account.issuer_label || (Number.isInteger(account.issuer) ? getIssuerDisplayName({ issuer: account.issuer!, issuer_label: account.issuer_label }) : undefined)],
		["Company access", privateView ? undefined : account.in_company ? "In this company" : "External"],
		["Name", details?.name],
		["Team", privateView ? undefined : account.buyer?.team_name || account.buyer?.team_uid],
		["Card", account.card?.mask],
		["BIN", account.card?.bin],
		["Currency", account.card?.currency],
		["Status", account.card?.status]
	].filter((row): row is [string, string] => Boolean(row[1]));
};

const Value = ({ children }: { children: ReactNode }) => (
	<Typo sx={{ fontSize: "14px", fontWeight: 600, color: DS.ink, textAlign: "right", overflowWrap: "anywhere" }}>{children}</Typo>
);

/** Quiet pill-shaped action used inside detail rows. */
const InlineAction = ({ label, onClick, quiet = false, children }: { label: string; onClick: () => void | Promise<void>; quiet?: boolean; children: ReactNode }) => (
	<Box
		element="button"
		type="button"
		aria-label={label}
		onClick={() => void onClick()}
		sx={{
			border: 0,
			borderRadius: "10px",
			padding: "6px 10px",
			minHeight: "32px",
			fontFamily: "inherit",
			fontSize: "13px",
			fontWeight: 600,
			cursor: "pointer",
			textAlign: "right",
			overflowWrap: "anywhere",
			color: quiet ? DS.muted : DS.accentInk,
			background: quiet ? "transparent" : DS.accentSoft,
			"&:hover": { background: quiet ? DS.soft : "#E0E4FF" },
			[DS.mobile]: { minHeight: "44px" },
		}}
	>
		{children}
	</Box>
);

type Leg = ITransactionDetails["trx"]["legs"][number];
const isOwnLeg = (leg: Leg) => Boolean(leg.account) && leg.account!.kind !== "external" && leg.account!.kind !== "merchant";

/** A list-shaped row rebuilt from operation details, for an operation opened by its id instead of from a list. */
export const transactionFromDetails = (details: ITransactionDetails): ICompanyTrxTransaction => {
	const legs = details.trx.legs;
	// The side that belongs to the company: the paying account, else the receiving one (a deposit).
	const own = legs.find(leg => isOwnLeg(leg) && leg.direction === "expense") ?? legs.find(isOwnLeg) ?? legs[0];
	const other = legs.find(leg => leg !== own);
	const card = own?.account?.card;
	const buyer = own?.account?.buyer;
	return {
		id: own?.id ?? details.operation_id,
		operation_id: details.operation_id,
		status: details.trx.status,
		direction: own?.direction ?? "expense",
		type_id: details.trx.type_id,
		amount: details.trx.amount,
		balance: own?.balance ?? 0,
		currency: details.trx.currency,
		description: details.trx.description,
		account_id: own?.account_id ?? 0,
		counterparty_account_id: other?.account_id ?? 0,
		created_at: details.trx.created_at,
		operation_type: details.trx.operation_type,
		merchant: details.trx.merchant,
		...(card ? { card_uid: card.uid, card_name: card.name, card_mask: card.mask } : {}),
		...(buyer ? { buyer_uid: buyer.uid, buyer_name: buyer.name, team_uid: buyer.team_uid, team_name: buyer.team_name } : {}),
		...(details.fee ? { charge: { fee_code: details.fee.fee_code, charged_by: details.fee.charged_by, label: details.fee.label, payer: details.fee.payer } } : {}),
	};
};

/** Details of an operation known only by its id: loads it, then shows it like one opened from a list. */
const TransactionDetailsByOperation = ({ operationId, onBack, privateCardActionsOnly }: { operationId: number; onBack?: () => void; privateCardActionsOnly?: boolean }) => {
	const details = useTransactionDetails(operationId);
	// ponytail: TransactionDetails reads the same details again; pass them down if this second read ever matters.
	if (details.data) return <TransactionDetails transaction={transactionFromDetails(details.data)} onBack={onBack} privateCardActionsOnly={privateCardActionsOnly} />;
	return (
		<VBox gap="16px" sx={{ minWidth: 0 }}>
			<TransactionDetailsLive {...details} privateView={privateCardActionsOnly} />
			<HBox sx={{ justifyContent: "flex-end" }}>
				<Button title={onBack ? "Back" : "Close"} variant="secondarySmall" onClick={onBack ?? (() => S_UNIVERSAL_DIALOG.invoke(null))} sx={{ minWidth: "80px", minHeight: "36px", [DS.mobile]: { minHeight: "44px" } }} />
			</HBox>
		</VBox>
	);
};

export const TransactionDetails = ({ transaction, typeOption, onClose, onBack, onTransactions, privateCardActionsOnly = false }: TransactionDetailsProps) => {
	const router = useRouter();
	const liveDetails = useTransactionDetails(transaction.operation_id);
	const amountPresentation = getTransactionAmountPresentation(transaction);
	const formattedAmount = formatCurrencyWithNumberToSymbolWithNumber(amountPresentation.amount, transaction.currency);
	const formattedBalance = formatCurrencyWithNumberToSymbolWithNumber(transaction.balance, transaction.currency);
	const transactionTypeLabel = getTransactionTypeLabel(transaction, typeOption);
	const isPrivateAccount = useIsPrivateAccount();
	const privateView = privateCardActionsOnly || isPrivateAccount;
	const transactionDescription = getTransactionDescription(transaction, typeOption, privateView);
	const parties = getTransactionParties(transaction, privateView);
	const parent = transaction.parent;
	// Live merchant fields win, but empty ones must not erase what the list row already had.
	const liveMerchant = liveDetails.data?.trx.merchant;
	const merchant = { ...transaction.merchant, ...Object.fromEntries(Object.entries(liveMerchant ?? {}).filter(([, value]) => value)) } as ITransactionMerchant;
	// Issuer raw data describes this payment only for card transactions; for an event fee it is the original purchase.
	const cardIssuerRaw = liveDetails.data?.issuer?.kind === "card_transaction" ? liveDetails.data.issuer.raw : undefined;
	const merchantName = merchant.name || issuerMerchantName(cardIssuerRaw);
	const isFee = Boolean(liveDetails.data?.fee || transaction.charge?.fee_code || transaction.operation_type === "commission");
	const formattedParentAmount = parent
		? formatCurrencyWithNumberToSymbolWithNumber(Math.abs(Number(parent.amount) || 0), parent.currency)
		: "";
	const closeDetails = onClose ?? (() => S_UNIVERSAL_DIALOG.invoke(null));

	// A fee row or the top-up behind a processing fee opens on top of these details; Back returns here.
	const openOperation = (operationId: number) => openNestedUniversalDialog(onBackToThis => ({
		label: "Transaction Details",
		hideHistoryBack: true,
		closeDialog: onBackToThis ?? backUniversalDialog,
		sx: { maxWidth: "600px", width: "100%" },
		content: <TransactionDetailsByOperation operationId={operationId} onBack={onBackToThis} privateCardActionsOnly={privateCardActionsOnly} />,
	}));

	const openAccountDetails = async (accountId: number) => {
		const response = await GD.REQ_COMPANY_TRANSACTION_ACCOUNT.request({ account_id: accountId });
		if (response.error || !response.data) {
			toast.error("Could not load account details");
			return;
		}
		S_UNIVERSAL_DIALOG.invoke({
			label: "Transaction Account",
			closeDialog: backUniversalDialog,
			sx: { maxWidth: "520px", width: "100%" },
			content: (
				<Box sx={{ padding: "20px" }}>
					<Section>
						{getAccountDetailsRows(response.data, privateCardActionsOnly).map(([label, value]) => (
							<DetailRow key={label} label={label} value={value} />
						))}
					</Section>
				</Box>
			)
		});
	};

	// A fee goes to the fee itself, not to the merchant of the original purchase.
	const externalName = isFee ? transactionDescription : merchantName || "External account";
	const partyName = (label: "From" | "To", party?: ICompanyTransactionParty) =>
		party && (party.kind === "external" || party.kind === "merchant")
			? externalName
			: (label === "From" ? parties.from : parties.to).replace(/ · (Account #\d+|Unknown account)$/, "");
	const renderParty = (label: "From" | "To", party?: ICompanyTransactionParty) =>
		party?.account_id ? (
			<Button
				variant="secondary"
				onClick={() => void openAccountDetails(party.account_id)}
				sx={{
					padding: "12px 14px",
					minHeight: "48px",
					fontSize: "0.875rem",
					fontWeight: 600,
					color: DS.ink,
					backgroundColor: DS.soft,
					border: `1px solid ${DS.line}`,
					borderRadius: "12px",
					[DS.mobile]: { minHeight: "48px" },
					justifyContent: "flex-start",
					textAlign: "left",
					width: "100%",
					minWidth: 0,
					whiteSpace: "normal",
					overflowWrap: "anywhere",
					lineHeight: 1.35
				}}
				ariaLabel={`Open ${label.toLowerCase()} account ${party.account_id}`}
			>
				{partyName(label, party)}
			</Button>
		) : (
			<Typo sx={{ fontSize: "14px", fontWeight: 600, color: DS.ink, padding: "12px 14px", borderRadius: "12px", background: DS.soft, border: `1px solid ${DS.line}`, overflowWrap: "anywhere" }}>
				{partyName(label, party)}
			</Typo>
		);

	const openBuyerDetails = () => {
		if (!transaction.buyer_uid) return;

		S_UNIVERSAL_DIALOG.invoke({
			sx: { maxWidth: MAX_USER_DETAILS_WIDTH },
			contentPadding: "0px",
			content: (
				<UserDetails
					userUid={transaction.buyer_uid}
					onClose={backUniversalDialog}
					onUpdate={async () => {
						await GD.REQ_USERS.request();
					}}
				/>
			),
			closeDialog: backUniversalDialog,
		});
	};

	const openTeamDetails = async () => {
		if (!transaction.team_uid) return;

		const response = await GD.REQ_TEAM_GET.request(transaction.team_uid);
		if (response.error || !response.data) {
			toast.error("Could not load team details");
			return;
		}

		S_UNIVERSAL_DIALOG.invoke({
			sx: {
				padding: "0px",
				width: `min(${TEAM_DETAILS_MAX_WIDTH}px, calc(100vw - 32px))`,
				maxWidth: `${TEAM_DETAILS_MAX_WIDTH}px`,
			},
			content: (
				<TeamDetails
					team={response.data}
					onClose={backUniversalDialog}
					onUpdate={async () => {
						await GD.REQ_TEAMS_LIST.request();
					}}
				/>
			),
			closeDialog: backUniversalDialog,
		});
	};

	const openCardDetails = async () => {
		if (!transaction.card_uid) return;

		const response = await GD.REQ_CARDS.request();
		if (response.error || !response.data) {
			toast.error("Could not load card details");
			return;
		}

		const card = response.data.find(item => item.uid === transaction.card_uid);
		if (!card) {
			toast.error("Card was not found");
			return;
		}

		openNestedUniversalDialog((onBack) => ({
			label: "Card Details",
			hideHistoryBack: true,
			closeDialog: onBack ?? backUniversalDialog,
			sx: {
				maxWidth: "850px",
				width: "100%",
			},
			content: (
				<Box sx={{ maxHeight: "85vh", overflowY: "auto" }}>
					<CardDetails
						card={card}
						onTransactions={onTransactions}
						onClose={() => S_UNIVERSAL_DIALOG.invoke(null)}
						onBack={onBack ?? backUniversalDialog}
					/>
				</Box>
			),
		}));
	};

	const relatedActions = [
		!privateCardActionsOnly && transaction.buyer_uid
			? {
					key: "buyer",
					title: "Show Buyer",
					onClick: openBuyerDetails,
				}
			: null,
		!privateCardActionsOnly && transaction.team_uid
			? {
					key: "team",
					title: "Show Team",
					onClick: openTeamDetails,
				}
			: null,
		!privateCardActionsOnly && transaction.team_uid
			? {
					key: "team-page",
					title: "Open Team Page",
					onClick: () => {
						S_UNIVERSAL_DIALOG.invoke(null);
						router.navigate("team_details", { teamId: transaction.team_uid! });
					},
				}
			: null,
		transaction.card_uid
			? {
					key: "card",
					title: "Show Card",
					onClick: openCardDetails,
				}
			: null,
		transaction.card_uid
			? {
					key: "card-transactions",
					title: "Card Transactions",
					onClick: () => {
						S_UNIVERSAL_DIALOG.invoke(null);
						if (onTransactions) {
							onTransactions(transaction.card_uid!);
							return;
						}
						const nextUrl = `/transactions?cardId=${encodeURIComponent(transaction.card_uid!)}`;
						if (window.location.pathname + window.location.search === nextUrl) return;
						history.pushState({}, "", nextUrl);
					},
				}
			: null,
	].filter(Boolean) as { key: string; title: string; onClick: () => void | Promise<void> }[];

	const entityRows = [
		...(privateCardActionsOnly ? [] : [
		{ key: "buyer", label: "Buyer", name: transaction.buyer_name || transaction.buyer_uid, uid: transaction.buyer_uid },
		{ key: "team", label: "Team", name: transaction.team_name || transaction.team_uid, uid: transaction.team_uid },
		]),
		{ key: "card", label: "Card", name: transaction.card_name || transaction.card_uid, uid: transaction.card_uid },
	].filter(row => row.name);

	const live = liveDetails.data;
	const income = transaction.direction === "income";
	const title = merchantName || transactionDescription;
	const maskedCard = getMaskedLastFour(transaction.card_mask);
	const meta = [transactionTypeLabel, formatDateTime(transaction.created_at), maskedCard && `Card ${maskedCard}`].filter(Boolean).join(" · ");
	const merchantRows: [string, string | null | undefined, boolean?][] = [
		["Category", merchant.category],
		["MCC", merchant.mcc, true],
		["Country", merchant.country],
		["State", merchant.state],
		["City", merchant.city],
		["ZIP", merchant.zip, true],
		["Merchant ID", merchant.mid, true],
		["Wallet", merchant.wallet ? formatMerchantWallet(merchant.wallet) : null],
	];
	const shownMerchantRows = [["Name", merchantName], ...merchantRows].filter(([, value]) => Boolean(value)) as typeof merchantRows;
	const identifierRows: [string, string | number | null | undefined][] = [
		["Operation ID", transaction.operation_id],
		["Parent trade no", transaction.parent_trade_no],
		["Reference ID", live?.trx.reference_id],
		["Order ID", live?.issuer?.order_id],
		...entityRows.filter(row => row.uid && row.uid !== row.name).map(row => [`${row.label} UID`, row.uid] as [string, string]),
	];
	const shownIdentifiers = identifierRows.filter(([, value]) => value !== null && value !== undefined && value !== "");
	const issuerLabel = live?.issuer?.issuer_label;

	return (
		<Box sx={{ minWidth: 0 }}>
			<VBox gap="16px">
				<EntityHeader
					stacked
					leading={<TxIcon type={transaction.operation_type} income={income} />}
					title={title}
					meta={meta}
					badge={<StatusPill status={transaction.status} />}
				/>

				<AmountHero
					income={income}
					label={income ? "Funds in" : "Funds out"}
					value={<>{amountPresentation.sign}{formattedAmount}</>}
					side={
						<VBox gap="2px" sx={{ alignItems: "flex-end", position: "relative" }}>
							<Typo sx={{ fontSize: "12px", color: "rgba(255,255,255,0.75)" }}>Balance after</Typo>
							<Typo sx={{ fontSize: "16px", fontWeight: 600, color: "#FFFFFF", fontVariantNumeric: "tabular-nums" }}>{formattedBalance}</Typo>
						</VBox>
					}
				/>

				<Section title="Payment">
					<DetailRow label="Amount" value={<Typo sx={{ fontSize: "14px", fontWeight: 700, color: amountPresentation.color, fontVariantNumeric: "tabular-nums" }}>{amountPresentation.sign}{formattedAmount}</Typo>} />
					<DetailRow label="Type" value={transactionTypeLabel} />
					{transactionDescription !== title && <DetailRow label="Description" value={transactionDescription} />}
					{formatDateTime(transaction.created_at) && <DetailRow label="Date and time" value={formatDateTime(transaction.created_at)} />}
					{transaction.currency && <DetailRow label="Currency" value={transaction.currency} />}
					{issuerAmountRows(cardIssuerRaw === undefined ? undefined : liveDetails.data?.issuer).map(([label, value]) => <DetailRow key={label} label={label} value={value} />)}
				</Section>

				<FundsFlow
					status={transaction.status}
					legs={live?.trx.legs}
					merchantName={externalName}
					privateView={privateView}
					onAccount={accountId => void openAccountDetails(accountId)}
					fallback={<MoneyFlow from={renderParty("From", transaction.from)} to={renderParty("To", transaction.to)} />}
				/>

				{shownMerchantRows.length > 0 && (
					<Section title="Merchant">
						{shownMerchantRows.map(([label, value, mono]) => <DetailRow key={label} label={label} value={value!} mono={mono} />)}
					</Section>
				)}

				{(entityRows.length > 0 || transaction.card_bin || transaction.card_mask || issuerLabel) && (
					<Section title="Card">
						{entityRows.map(row => {
							const action = relatedActions.find(item => item.key === row.key);
							const extra = relatedActions.find(item => item.key === (row.key === "team" ? "team-page" : row.key === "card" ? "card-transactions" : ""));
							return (
								<DetailRow
									key={row.key}
									label={row.label}
									value={
										<HBox gap="6px" sx={{ justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap", minWidth: 0 }}>
											{action ? <InlineAction label={action.title} onClick={action.onClick}>{row.name} <span aria-hidden="true">↗</span></InlineAction> : <Value>{row.name}</Value>}
											{extra && <InlineAction quiet label={extra.title} onClick={extra.onClick}>{extra.title} <span aria-hidden="true">→</span></InlineAction>}
										</HBox>
									}
								/>
							);
						})}
						{transaction.card_bin && <DetailRow label="BIN" value={transaction.card_bin} mono />}
						{transaction.card_mask && <DetailRow label="Mask" value={transaction.card_mask} mono />}
						{issuerLabel && <DetailRow label="Issuer" value={issuerLabel} />}
					</Section>
				)}

				{parent && (
					<Section title="Original transaction" action={<StatusPill status={parent.status} />}>
						{parent.trade_no && <DetailRow label="Trade no" value={parent.trade_no} mono copy={parent.trade_no} />}
						<DetailRow label="Amount" value={<Typo sx={{ fontSize: "14px", fontWeight: 700, color: parent.direction === "income" ? "#067647" : "#DC2626", fontVariantNumeric: "tabular-nums" }}>{parent.direction === "income" ? "+" : "−"}{formattedParentAmount}</Typo>} />
						{formatDateTime(parent.transaction_time) && <DetailRow label="Date and time" value={formatDateTime(parent.transaction_time)} />}
						{parent.type && <DetailRow label="Type" value={getParentTypeLabel(parent.type)} />}
						{parent.merchant && <DetailRow label="Merchant" value={parent.merchant} />}
						{parent.reason && <DetailRow label="Decline reason" value={parent.reason} />}
						{parent.operation_id !== null && parent.operation_id !== undefined && <DetailRow label="Operation ID" value={parent.operation_id} mono copy={String(parent.operation_id)} />}
						{parent.description && parent.description !== parent.reason && <DetailRow label="Description" value={parent.description} />}
					</Section>
				)}

				{shownIdentifiers.length > 0 && (
					<Section title="Identifiers">
						{shownIdentifiers.map(([label, value]) => <DetailRow key={label} label={label} value={value!} mono copy={String(value)} />)}
					</Section>
				)}

				<TransactionDetailsLive {...liveDetails} transaction={transaction} privateView={privateCardActionsOnly} onOpenOperation={openOperation} />

				<HBox sx={{ justifyContent: "flex-end", paddingTop: "2px" }}>
					<Button
						title={onBack ? "Back" : "Close"}
						ariaLabel={onBack ? "Back to previous dialog" : "Close transaction details"}
						variant="secondarySmall"
						onClick={onBack ?? closeDetails}
						sx={{ minWidth: "80px", minHeight: "36px", [DS.mobile]: { minHeight: "44px" } }}
					/>
				</HBox>
			</VBox>
		</Box>
	);
};
